import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import { FaSearch, FaCalendar, FaSortDown } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { SERVER_BASE_URL } from '../../utils/api';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px 40px;
  color: #fff;
  text-align: center;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4);
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 24px 24px;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const SummaryCard = styled.div`
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid ${props => props.color || '#667eea'};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const SummaryCardTitle = styled.div`
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  font-weight: 600;
`;

const SummaryCardValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || '#333'};
  font-family: 'Courier New', monospace;
`;

const SummaryCardLabel = styled.div`
  font-size: 0.85rem;
  color: #999;
  margin-top: 8px;
`;

const TableWrapper = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  overflow: hidden;
  margin-bottom: 32px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
`;

const Tr = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background: linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%);
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const CurrencyCell = styled.td`
  padding: 16px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  font-weight: 500;
  
  &:first-child {
    padding-left: 40px;
  }
`;

const NumberCell = styled.td`
  padding: 16px 12px;
  border-bottom: 1px solid #e9ecef;
  color: ${props => {
    if (props.positive) return '#28a745';
    if (props.negative) return '#dc3545';
    return '#333';
  }};
  font-family: 'Courier New', monospace;
  font-weight: ${props => (props.positive || props.negative) ? '600' : '400'};
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
`;

const OperationsContainer = styled.div`
  margin-top: 40px;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  white-space: nowrap;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ShowButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const GroupArea = styled.div`
  background: #f8f9fa;
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
  min-height: 50px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  
  &.drag-over {
    border-color: #ff6b35;
    background: #fff3f0;
  }
`;

const GroupTag = styled.div`
  background: #ff6b35;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  
  &:hover {
    background: #e55a2b;
  }
`;

const RemoveIcon = styled.span`
  font-size: 10px;
  cursor: pointer;
  
  &:hover {
    color: #ffebee;
  }
`;

const SearchBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 35px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 200px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  color: #666;
  font-size: 12px;
`;

const OperationsTableWrapper = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  overflow: hidden;
  margin-top: 20px;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 30px rgba(0,0,0,0.12);
  }
`;

const OperationsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const OperationsTh = styled.th`
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  cursor: grab;
  user-select: none;
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
  
  &:active {
    cursor: grabbing;
  }
  
  &.dragging {
    opacity: 0.5;
  }
`;

const OperationsThRight = styled.th`
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: right;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  cursor: grab;
  user-select: none;
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
  
  &:active {
    cursor: grabbing;
  }
  
  &.dragging {
    opacity: 0.5;
  }
`;

const OperationsTd = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
`;

const NumberCellRight = styled.td`
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  text-align: right;
  font-family: 'Courier New', monospace;
  
  &:first-child {
    padding-left: 40px;
  }
  
  &:last-child {
    padding-right: 12px;
  }
`;

const FilterRow = styled.tr`
  background: #f8f9fa;
`;

const FilterCell = styled.td`
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  
  &:first-child {
    padding-left: 40px;
  }
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 11px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const SortIcon = styled(FaSortDown)`
  margin-left: 4px;
  color: #666;
  font-size: 10px;
`;

const CalendarIcon = styled(FaCalendar)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 10px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 1.1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  padding: 20px;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff'};
  color: ${props => props.active ? '#fff' : '#333'};
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? 'linear-gradient(135deg, #5568d3 0%, #653a91 100%)' : '#f8f9fa'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
  margin: 0 10px;
  white-space: nowrap;
`;

const formatNumber = (num) => {
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatNumberSimple = (num) => {
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });
};

const Finances = () => {
  const { isAuthenticated } = useAuth();
  const [operationsCount, setOperationsCount] = useState(20);
  const [groupedColumns, setGroupedColumns] = useState([]);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [displayedOperations, setDisplayedOperations] = useState(20);
  
  // State for API data
  const [operationsData, setOperationsData] = useState([]);
  const [financesData, setFinancesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filters and search
  const [filters, setFilters] = useState({
    date: '',
    note: '',
    incomeP: '',
    expenseP: '',
    cumulativeP: '',
    incomeBonus: '',
    expenseBonus: '',
    cumulativeBonus: '',
    account: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;

  // Fetch sales journal data from API
  useEffect(() => {
    const fetchSalesData = async () => {
      if (!isAuthenticated()) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${SERVER_BASE_URL}/api/orders/sales-journal?limit=100`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data) {
            // Получаем операции из API (уже в правильном формате)
            const operationsFromAPI = data.data.operations || [];
            
            // Преобразуем операции в формат для отображения
            const operations = operationsFromAPI.map(op => {
              const opDate = new Date(op.date);
              const formattedDate = opDate.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
              
              return {
                date: formattedDate,
                note: op.note || '',
                incomeP: op.incomeP || 0,
                expenseP: op.expenseP || 0,
                cumulativeP: Math.max(0, op.cumulativeP || 0), // Гарантируем неотрицательный баланс
                incomeBonus: op.incomeBonus || 0,
                expenseBonus: op.expenseBonus || 0,
                cumulativeBonus: Math.max(0, op.cumulativeBonus || 0), // Гарантируем неотрицательный баланс
                account: op.account || '',
                orderId: op.orderId,
                rawDate: opDate
              };
            });
            
            setOperationsData(operations);
            
            // Используем summary из API (уже правильно рассчитан)
            if (data.data.summary && data.data.summary.currency) {
              const summaryData = data.data.summary.currency.map(item => ({
                currency: item.currency,
                turnoverIncome: item.turnoverIncome || 0,
                turnoverExpense: item.turnoverExpense || 0,
                balance: Math.max(0, item.balance || 0) // Гарантируем неотрицательный баланс
              }));
              setFinancesData(summaryData);
            } else {
              // Fallback: рассчитываем сами, если summary нет
              const totalIncomeP = operations.reduce((sum, op) => sum + (op.incomeP || 0), 0);
              const totalExpenseP = operations.reduce((sum, op) => sum + (op.expenseP || 0), 0);
              const totalIncomeBonus = operations.reduce((sum, op) => sum + (op.incomeBonus || 0), 0);
              const totalExpenseBonus = operations.reduce((sum, op) => sum + (op.expenseBonus || 0), 0);
              
              setFinancesData([
                {
                  currency: 'P',
                  turnoverIncome: totalIncomeP,
                  turnoverExpense: totalExpenseP,
                  balance: Math.max(0, totalIncomeP - totalExpenseP)
                },
                {
                  currency: 'Бонус',
                  turnoverIncome: totalIncomeBonus,
                  turnoverExpense: totalExpenseBonus,
                  balance: Math.max(0, totalIncomeBonus - totalExpenseBonus)
                }
              ]);
            }
          }
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Ошибка загрузки данных журнала продаж');
        }
      } catch (err) {
        console.error('Error loading sales journal:', err);
        setError('Ошибка сети при загрузке данных');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [isAuthenticated]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Function to get value from operation by column name
  const getColumnValue = (operation, columnName) => {
    const columnMap = {
      'Учетная дата': operation.date,
      'Примечание': operation.note,
      'Приход P': operation.incomeP,
      'Расход P': operation.expenseP,
      'Нарастающий итог P': operation.cumulativeP,
      'Приход Бонус': operation.incomeBonus,
      'Расход Бонус': operation.expenseBonus,
      'Нарастающий итог Бонус': operation.cumulativeBonus,
      'Корреспондирующий счет': operation.account
    };
    return columnMap[columnName] || '';
  };

  // Function to group operations by column
  const groupOperationsByColumn = (operations, columnName) => {
    const groups = {};
    operations.forEach(op => {
      const key = String(getColumnValue(op, columnName));
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(op);
    });
    return groups;
  };

  // Function to apply grouping
  const applyGrouping = (operations) => {
    if (groupedColumns.length === 0) {
      return operations;
    }

    // Recursive function for nested grouping
    const groupRecursive = (ops, columns, level = 0) => {
      if (columns.length === 0 || ops.length === 0) {
        return ops;
      }

      const columnName = columns[0];
      const groups = groupOperationsByColumn(ops, columnName);
      const result = [];

      // Sort group keys
      const sortedKeys = Object.keys(groups).sort((a, b) => {
        // Try to sort as numbers if possible
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numB - numA; // Descending for numbers
        }
        return a.localeCompare(b);
      });

      sortedKeys.forEach(key => {
        const groupOps = groups[key];
        
        // Recursively group remaining columns
        const remainingColumns = columns.slice(1);
        let processedOps;
        if (remainingColumns.length > 0) {
          processedOps = groupRecursive(groupOps, remainingColumns, level + 1);
        } else {
          processedOps = groupOps.map(op => ({ ...op, groupLevel: level }));
        }
        
        // Count actual operations (not group headers)
        const operationCount = processedOps.filter(op => op.type !== 'group-header').length;
        
        result.push({ 
          type: 'group-header', 
          column: columnName, 
          value: key, 
          level: level,
          count: operationCount
        });
        
        result.push(...processedOps);
      });

      return result;
    };

    return groupRecursive(operations, groupedColumns);
  };

  // Function to filter operations
  const filterOperations = (operations) => {
    return operations.filter(op => {
      // Skip group headers
      if (op.type === 'group-header') return true;

      // Filter by date
      if (filters.date && !op.date.includes(filters.date)) return false;
      
      // Filter by note
      if (filters.note && !op.note.toLowerCase().includes(filters.note.toLowerCase())) return false;
      
      // Filter by incomeP
      if (filters.incomeP && String(op.incomeP || 0) !== filters.incomeP) return false;
      
      // Filter by expenseP
      if (filters.expenseP && String(op.expenseP || 0) !== filters.expenseP) return false;
      
      // Filter by cumulativeP
      if (filters.cumulativeP && String(op.cumulativeP || 0) !== filters.cumulativeP) return false;
      
      // Filter by incomeBonus
      if (filters.incomeBonus && String(op.incomeBonus || 0) !== filters.incomeBonus) return false;
      
      // Filter by expenseBonus
      if (filters.expenseBonus && String(op.expenseBonus || 0) !== filters.expenseBonus) return false;
      
      // Filter by cumulativeBonus
      if (filters.cumulativeBonus && String(op.cumulativeBonus || 0) !== filters.cumulativeBonus) return false;
      
      // Filter by account
      if (filters.account && !op.account.toLowerCase().includes(filters.account.toLowerCase())) return false;
      
      return true;
    });
  };

  // Function to search operations
  const searchOperations = (operations) => {
    if (!debouncedSearchQuery.trim()) {
      return operations;
    }
    
    const query = debouncedSearchQuery.toLowerCase();
    return operations.filter(op => {
      // Skip group headers
      if (op.type === 'group-header') return true;
      
      return (
        op.date.toLowerCase().includes(query) ||
        op.note.toLowerCase().includes(query) ||
        op.account.toLowerCase().includes(query) ||
        String(op.incomeP || 0).includes(query) ||
        String(op.expenseP || 0).includes(query) ||
        String(op.incomeBonus || 0).includes(query) ||
        String(op.expenseBonus || 0).includes(query)
      );
    });
  };

  const handleDragStart = (e, columnName) => {
    setDraggedColumn(columnName);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    setDraggedColumn(null);
    e.target.classList.remove('dragging');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (draggedColumn && !groupedColumns.includes(draggedColumn)) {
      setGroupedColumns([...groupedColumns, draggedColumn]);
    }
    setDraggedColumn(null);
  };

  const removeGroupedColumn = (columnName) => {
    setGroupedColumns(groupedColumns.filter(col => col !== columnName));
  };

  const handleShowOperations = () => {
    setDisplayedOperations(operationsCount);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Сортируем операции по дате (новые сначала) и берем указанное количество
  let processedOperations = [...operationsData].sort((a, b) => {
    // Use rawDate if available, otherwise parse the date string
    const dateA = a.rawDate || new Date(a.date.split('.').reverse().join('-'));
    const dateB = b.rawDate || new Date(b.date.split('.').reverse().join('-'));
    return dateB - dateA;
  }).slice(0, displayedOperations);

  // Apply search first
  processedOperations = searchOperations(processedOperations);
  
  // Apply filters
  processedOperations = filterOperations(processedOperations);
  
  // Apply grouping (should be before pagination, as grouping needs all data)
  const groupedOperations = applyGrouping(processedOperations);
  
  // Calculate pagination for grouped operations
  const totalItems = groupedOperations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Apply pagination to grouped operations
  const sortedOperations = groupedOperations.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters/search/grouping changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearchQuery, groupedColumns]);

  return (
    <>
      <DashboardHeader />
      <Container>
        <Title>Журнал продаж</Title>
        
        {loading ? (
          <LoadingMessage>Загрузка данных журнала продаж...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <>
            <SummaryCards>
              {financesData.map((row, index) => (
                <SummaryCard key={index} color={row.currency === 'P' ? '#667eea' : '#f093fb'}>
                  <SummaryCardTitle>{row.currency}</SummaryCardTitle>
                  <SummaryCardValue color={row.currency === 'P' ? '#667eea' : '#f093fb'}>
                    {formatNumber(row.balance)}
                  </SummaryCardValue>
                  <SummaryCardLabel>
                    Приход: {formatNumber(row.turnoverIncome)} | Расход: {formatNumber(row.turnoverExpense)}
                  </SummaryCardLabel>
                </SummaryCard>
              ))}
            </SummaryCards>

            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Валюта</Th>
                    <Th>Оборот приход</Th>
                    <Th>Оборот расход</Th>
                    <Th>Остаток</Th>
                  </tr>
                </thead>
                <tbody>
                  {financesData.map((row, index) => (
                    <Tr key={index}>
                      <CurrencyCell>{row.currency}</CurrencyCell>
                      <NumberCell positive>{formatNumber(row.turnoverIncome)}</NumberCell>
                      <NumberCell negative>{formatNumber(row.turnoverExpense)}</NumberCell>
                      <NumberCell positive={row.balance > 0}>{formatNumber(row.balance)}</NumberCell>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

        <OperationsContainer>
          <ControlsRow>
            <ControlGroup>
              <Label>Количество последних операций</Label>
              <Input 
                type="number" 
                value={operationsCount} 
                onChange={(e) => setOperationsCount(e.target.value)}
                style={{ width: '80px' }}
              />
              <ShowButton onClick={handleShowOperations}>ПОКАЗАТЬ</ShowButton>
            </ControlGroup>
            <SearchBar>
              <SearchIcon />
              <SearchInput 
                placeholder="Искать..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          </ControlsRow>

          <GroupArea 
            className={isDragOver ? 'drag-over' : ''}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {groupedColumns.length === 0 ? (
              'Перетащите столбец сюда, чтобы сгруппировать по нему'
            ) : (
              groupedColumns.map((column, index) => (
                <GroupTag key={index}>
                  {column}
                  <RemoveIcon onClick={() => removeGroupedColumn(column)}>×</RemoveIcon>
                </GroupTag>
              ))
            )}
          </GroupArea>

          <OperationsTableWrapper>
            <OperationsTable>
              <thead>
                <tr>
                  <OperationsTh
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Учетная дата')}
                    onDragEnd={handleDragEnd}
                  >
                    Учетная дата
                    <SortIcon />
                  </OperationsTh>
                  <OperationsTh
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Примечание')}
                    onDragEnd={handleDragEnd}
                  >
                    Примечание
                    <SortIcon />
                  </OperationsTh>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Приход P')}
                    onDragEnd={handleDragEnd}
                  >
                    Приход P
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Расход P')}
                    onDragEnd={handleDragEnd}
                  >
                    Расход P
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Нарастающий итог P')}
                    onDragEnd={handleDragEnd}
                  >
                    Нарастающий итог P
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Приход Бонус')}
                    onDragEnd={handleDragEnd}
                  >
                    Приход Бонус
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Расход Бонус')}
                    onDragEnd={handleDragEnd}
                  >
                    Расход Бонус
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsThRight
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Нарастающий итог Бонус')}
                    onDragEnd={handleDragEnd}
                  >
                    Нарастающий итог Бонус
                    <SortIcon />
                  </OperationsThRight>
                  <OperationsTh
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'Корреспондирующий счет')}
                    onDragEnd={handleDragEnd}
                  >
                    Корреспондирующий счет
                    <SortIcon />
                  </OperationsTh>
                </tr>
                <FilterRow>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.date}
                      onChange={(e) => handleFilterChange('date', e.target.value)}
                    />
                    <CalendarIcon />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.note}
                      onChange={(e) => handleFilterChange('note', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.incomeP}
                      onChange={(e) => handleFilterChange('incomeP', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.expenseP}
                      onChange={(e) => handleFilterChange('expenseP', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.cumulativeP}
                      onChange={(e) => handleFilterChange('cumulativeP', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.incomeBonus}
                      onChange={(e) => handleFilterChange('incomeBonus', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.expenseBonus}
                      onChange={(e) => handleFilterChange('expenseBonus', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.cumulativeBonus}
                      onChange={(e) => handleFilterChange('cumulativeBonus', e.target.value)}
                    />
                  </FilterCell>
                  <FilterCell>
                    <FilterInput 
                      placeholder="Фильтр" 
                      value={filters.account}
                      onChange={(e) => handleFilterChange('account', e.target.value)}
                    />
                  </FilterCell>
                </FilterRow>
              </thead>
              <tbody>
                {sortedOperations.length === 0 ? (
                  <Tr>
                    <OperationsTd colSpan="9">
                      <EmptyMessage>Нет данных об операциях</EmptyMessage>
                    </OperationsTd>
                  </Tr>
                ) : (
                  sortedOperations.map((row, index) => {
                    // Если это заголовок группы
                    if (row.type === 'group-header') {
                      const paddingLeft = (row.level || 0) * 30;
                      const bgColors = ['#e3f2fd', '#fff3e0', '#f3e5f5', '#e8f5e9'];
                      const bgColor = bgColors[row.level % bgColors.length];
                      return (
                        <Tr key={`group-${index}`} style={{ 
                          background: bgColor, 
                          fontWeight: 'bold',
                          borderTop: row.level === 0 ? '2px solid #667eea' : '1px solid #ddd'
                        }}>
                          <OperationsTd colSpan="9" style={{ 
                            paddingLeft: `${paddingLeft + 40}px`,
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            fontSize: '0.95rem'
                          }}>
                            📊 {row.column}: <strong>{row.value}</strong> ({row.count || 0} {row.count === 1 ? 'операция' : row.count < 5 ? 'операции' : 'операций'})
                          </OperationsTd>
                        </Tr>
                      );
                    }
                    
                    // Обычная строка операции
                    const paddingLeft = (row.groupLevel || 0) * 20;
                    return (
                      <Tr key={index} style={{ paddingLeft: `${paddingLeft}px` }}>
                        <OperationsTd style={{ paddingLeft: `${paddingLeft + 40}px` }}>{row.date}</OperationsTd>
                        <OperationsTd>{row.note}</OperationsTd>
                        <NumberCellRight>{row.incomeP > 0 ? formatNumberSimple(row.incomeP) : ''}</NumberCellRight>
                        <NumberCellRight>{row.expenseP > 0 ? formatNumberSimple(row.expenseP) : ''}</NumberCellRight>
                        <NumberCellRight>{row.cumulativeP > 0 ? formatNumberSimple(row.cumulativeP) : ''}</NumberCellRight>
                        <NumberCellRight>{row.incomeBonus > 0 ? formatNumberSimple(row.incomeBonus) : ''}</NumberCellRight>
                        <NumberCellRight>{row.expenseBonus > 0 ? formatNumberSimple(row.expenseBonus) : ''}</NumberCellRight>
                        <NumberCellRight>{row.cumulativeBonus > 0 ? formatNumberSimple(row.cumulativeBonus) : ''}</NumberCellRight>
                        <OperationsTd>{row.account}</OperationsTd>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </OperationsTable>
          </OperationsTableWrapper>
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </PaginationButton>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <PaginationButton
                    key={pageNum}
                    active={currentPage === pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PaginationButton>
                );
              })}
              
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </PaginationButton>
              
              <PaginationInfo>
                Страница {currentPage} из {totalPages} ({totalItems} {totalItems === 1 ? 'запись' : totalItems < 5 ? 'записи' : 'записей'})
              </PaginationInfo>
            </PaginationContainer>
          )}
        </OperationsContainer>
          </>
        )}
      </Container>
      <DashboardFooter />
    </>
  );
};

export default Finances; 